import { useEffect, useState, useRef } from "react";
import config from "../../../config/config.json";
import { apiCall } from "../../../_services/apiCall";
import QuestionFormField from "./QuestionFormField";
import { detailsToSchema, schemaToDetails } from "./TableSchemaBuilder/schemaDetails";

const CreateSectorQuestionModal = (props) => {
  const dropdownItems = [
    { display: "One Time", value: "ONE_TIME" },
    { display: "Every Financial Year", value: "EVERY_FY" },
    { display: "Custom", value: "CUSTOM" },
  ];

  const {
    module,
    financial_year_id,
    allFrameworks,
    allIndustries,
    unitSettings,
    iso_code,
    onSuccess,
    isDerived,
  } = props;

  const [financialYearData, setFinancialYearData] = useState([]);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(
    props.existingQuestion?.financialYearId ?? null
  );
  const [fySearch, setFySearch] = useState("");
  const [fyDropdownOpen, setFyDropdownOpen] = useState(false);
  const [unitMaster, setUnitMaster] = useState([]);

  const fyDropdownRef = useRef(null);

  useEffect(() => {
    const h = e => { if (fyDropdownRef.current && !fyDropdownRef.current.contains(e.target)) setFyDropdownOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const getFinancialYearData = async (tmpIsoCode) => {
    const body = {
      iso_code: "ALL",
    };
    const { isSuccess, data } = await apiCall(
      `${config.ADMIN_API_URL}getAllFinancialYear`,
      {},
      body
    );
    if (isSuccess) {
      setFinancialYearData(data?.data);
    }
  };

  const fetchUnitMaster = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.ADMIN_API_URL}getUnitMaster`,
      {},
      {},
      "GET"
    );
    if (isSuccess) setUnitMaster(data || []);
  };

  useEffect(() => {
    getFinancialYearData(iso_code);
    fetchUnitMaster();
  }, [iso_code]);

  const filteredFyData = (financialYearData || []).filter(fy =>
    `${fy.financial_year_value} ${fy.country_name || ""}`.toLowerCase().includes(fySearch.toLowerCase())
  );

  const selectedFyLabel = selectedFinancialYear
    ? (() => { const f = financialYearData.find(fy => fy.id === selectedFinancialYear); return f ? `${f.financial_year_value} (${f.country_name})` : ""; })()
    : "";

  const normaliseTags = (raw) => {
    if (!raw) return {};
    if (typeof raw === "object" && !Array.isArray(raw)) return raw;

    if (Array.isArray(raw)) {
      const obj = {};
      const flat = Array.isArray(raw[0]) ? raw.flat() : raw;
      flat.forEach(t => {
        if (t && t.key) {
          if (!obj[t.key]) obj[t.key] = [];
          obj[t.key].push(t);
        }
      });
      return obj;
    }
    return {};
  };

  const transformExistingQuestionForEdit = (questionData) => {
    const tranformQuestionDetailsForEdit = () => {
      const storedOrder = [...(questionData.details || [])].reverse();
      if (["yes_no", "tabular_question"].includes(questionData.questionType)) return detailsToSchema(questionData.questionType, storedOrder);
      return storedOrder;
    };

    // Parse supported_units — backend returns it as camelCase `supportedUnits` (already parsed array)
    const parseSupportedUnits = (raw) => {
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      try { return JSON.parse(raw); } catch { return []; }
    };

    return {
      question_id: questionData.questionId,           // ← needed so backend updates by id
      question_type: questionData.questionType,
      frequency: questionData.frequency,
      question_title: questionData.title,
      default_description: questionData.defaultDescription ?? "",
      year_description: questionData.yearDescription ?? "",
      question_details: tranformQuestionDetailsForEdit(),
      applicable_check: questionData.applicableCheck,
      applicable_note: questionData.applicableNote,
      triger_check: questionData.trigerCheck,
      category: questionData.category,
      map_framework_ids: questionData.mapFrameworkIds,
      industry_ids: questionData.industryIds,
      is_formula_based: questionData.isFormulaBased,
      is_read_only: questionData.isReadOnly ?? null,
      formula: questionData.formula,
      framework_section_mapping: questionData.frameworkSectionMapping,
      variables_json: questionData.variablesJson,
      variable_units_json: questionData.variableUnitsJson || {},
      status: questionData.status,
      kpi_status: questionData.kpi_status,
      kpi_type: questionData.kpi_type,
      aggregation_method: questionData.aggregation_method,
      emission_category: questionData.emission_category,
      emission_category_id: questionData.emission_category_id,
      tags: normaliseTags(questionData.tags),
      // `supportedUnits` is the camelCase key returned by the backend
      supported_units: parseSupportedUnits(questionData.supportedUnits || questionData.supported_units),
      required_evidences: questionData.requiredEvidences,
    };
  };

  const emptyQuestion = {
    question_type: "",
    frequency: "",
    question_title: "",
    default_description: "",
    year_description: "",
    question_details: [],
    applicable_check: 1,
    applicable_note: 1,
    triger_check: 0,
    category: "",
    map_framework_ids: [],
    industry_ids: [],
    is_formula_based: 0,
    is_read_only: null,
    variable_units_json: {},
    framework_section_mapping: "",
    status: 0,
    tags: {},
    supported_units: [],
    required_evidences: [],
    aggregation_method: "SUM",
  };

  const [inputFields, setInputFields] = useState(
    props.existingQuestion
      ? [transformExistingQuestionForEdit(props.existingQuestion)]
      : [emptyQuestion]
  );

  // Re-sync inputFields whenever existingQuestion changes (e.g. modal opens in edit mode)
  // Use questionId as dependency (primitive) so React reliably detects the change
  useEffect(() => {
    if (props.existingQuestion) {
      setInputFields([transformExistingQuestionForEdit(props.existingQuestion)]);
    } else {
      setInputFields([{ ...emptyQuestion }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.existingQuestion?.questionId]);

  // ── Form handlers ─────────────────────────────────────────────────────────
  const formHandlers = {
    handleChange: (event, index) => {
      const values = [...inputFields];
      values[index][event.target.name] = event.target.value;
      if (event.target.name === "question_type") {
        values[index].question_details = [];
      } else if (event.target.name === "question_title" && ["quantitative", "quantitative_trends"].includes(values[index].question_type)) {
        values[index].question_details = [{ detail_id: 1, option_type: "column", option: event.target.value, rules: null }];
      }
      setInputFields(values);
    },

    handleIndustryChange: (selectedIndustryIds, index) => {
      const values = [...inputFields];
      values[index].industry_ids = [...new Set(selectedIndustryIds)];
      setInputFields(values);
    },

    handleKPiStatusChange: (event, index) => {
      const values = [...inputFields];
      values[index].kpi_status = event.target.value;
      setInputFields(values);
    },

    handleKPITypeChange: (selectedValues, index) => {
      const values = [...inputFields];
      values[index].kpi_type = selectedValues.length > 0 ? selectedValues : null;
      setInputFields(values);
    },

    handleFrameworkChange: (selectedFrameworkIds, index) => {
      const values = [...inputFields];
      values[index].map_framework_ids = [...new Set(selectedFrameworkIds)];
      setInputFields(values);
    },

    handleUnitCategoryChange: (event, index) => {
      const values = [...inputFields];
      if (["quantitative", "quantitative_trends"].includes(values[index].question_type)) {
        values[index].question_details = [{ detail_id: 1, option_type: "column", option: values[index].question_title, unit_id: event.target.value, rules: null }];
      }
      setInputFields(values);
    },

    handleIsFormulaBased: (event, index) => {
      const values = [...inputFields];
      if (event.target.value !== 1) { values[index].formula = null; values[index].variables_json = []; values[index].variable_units_json = {}; }
      values[index].is_formula_based = event.target.value;
      setInputFields(values);
    },

    handleQuestionDetailLabelChange: (event, key, i, index) => {
      event.preventDefault();
      const values = [...inputFields];
      values[index].question_details[key][i] = event.target.value;
      setInputFields(values);
    },

    handleTabularSchemaChange: (tableSchema, index) => {
      const values = [...inputFields];
      const details = schemaToDetails(tableSchema);
      if (details) values[index].question_details = details;
      setInputFields(values);
    },

    handleAdd: () => setInputFields([...inputFields, { ...emptyQuestion }]),

    handleRemove: (index) => {
      if (inputFields.length !== 1) {
        const values = [...inputFields];
        values.splice(index, 1);
        setInputFields(values);
      }
    },

    handleSupportedUnitsChange: (supportedUnits, index) => {
      const values = [...inputFields];
      values[index].supported_units = supportedUnits;
      setInputFields(values);
    },
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const { isSuccess } = await apiCall(
      `${config.ADMIN_API_URL}saveReportingQuestion`,
      {},
      { module_id: module?.id, financial_year_id: selectedFinancialYear || null, questions: inputFields },
      "POST"
    );
    if (isSuccess && onSuccess) onSuccess();
  };

  const createAssessmentQuestion = async (e) => {
    e.preventDefault();
    await apiCall(
      `${config.ADMIN_API_URL}createAssessmentQuestion`,
      {},
      { entity: "supplier", financialYearId: selectedFinancialYear || financial_year_id?.id, questions: JSON.stringify(inputFields) },
      "POST"
    );
  };

  return (
    <form name="form" onSubmit={e => module === "SUPPLIER_QUESTION_LIST" ? createAssessmentQuestion(e) : handleSubmit(e)}>
      <fieldset disabled={isDerived}>
      {isDerived && <div className="alert alert-info mx-3 mt-2 mb-0 py-2" style={{ fontSize: 13 }}>This is an auto-created question. To make changes, edit the parent question.</div>}
      <div className="business_detail">
        <div className="py-2 px-3"><p className="m-0"><b>{module.title}</b></p></div>
        <div className="px-3 py-2">
          <div className="mb-3">
            <label className="form-label fw-semibold text-dark"><i className="fas fa-calendar text-primary me-2"></i> Financial Year*</label>
            <div ref={fyDropdownRef} style={{ position: "relative" }}>
              <div onClick={() => setFyDropdownOpen(p => !p)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", border: "1px solid #dee2e6", borderRadius: 6, background: "#fff", cursor: "pointer", minHeight: 38, gap: 8 }}>
                <span style={{ flex: 1, color: selectedFyLabel ? "#212529" : "#9ca3af", fontSize: 14 }}>{selectedFyLabel || "Select Financial Year"}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  {selectedFinancialYear && <button type="button" onClick={e => { e.stopPropagation(); setSelectedFinancialYear(null); setFySearch(""); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 13, padding: "0 2px" }}>✕</button>}
                  <span style={{ fontSize: 10, color: "#9ca3af", display: "inline-block", transition: "transform 0.15s", transform: fyDropdownOpen ? "rotate(180deg)" : "none" }}>▼</span>
                </div>
              </div>
              {fyDropdownOpen && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 8, zIndex: 300, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden" }}>
                  <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
                    <input autoFocus type="text" className="form-control form-control-sm" placeholder="Search financial year..." value={fySearch} onChange={e => setFySearch(e.target.value)} onClick={e => e.stopPropagation()} style={{ fontSize: 13 }} />
                  </div>
                  <div style={{ maxHeight: 220, overflowY: "auto" }}>
                    {filteredFyData.length === 0
                      ? <div style={{ padding: "12px 14px", fontSize: 13, color: "#9ca3af" }}>No results found</div>
                      : filteredFyData.map(fy => {
                        const isSelected = selectedFinancialYear === fy.id;
                        return (
                          <div key={fy.id} onClick={() => { setSelectedFinancialYear(fy.id); setFySearch(""); setFyDropdownOpen(false); }} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", cursor: "pointer", background: isSelected ? "#f0f9ff" : "#fff", borderBottom: "1px solid #f8fafc", transition: "background 0.1s" }}>
                            <div>
                              <span style={{ fontSize: 13, fontWeight: isSelected ? 600 : 400, color: isSelected ? "#0369a1" : "#374151" }}>{fy.financial_year_value}</span>
                              {fy.country_name && <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 6 }}>({fy.country_name})</span>}
                            </div>
                            {isSelected && <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7l3.5 3.5L12 3" stroke="#0369a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                          </div>
                        );
                      })
                    }
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {inputFields?.map((item, index) => (
          <QuestionFormField
            key={index}
            item={item}
            index={index}
            dropdownItems={dropdownItems}
            inputFieldsLength={inputFields.length}
            allFrameworks={allFrameworks}
            allIndustries={allIndustries}
            unitSettings={unitSettings}
            unitMaster={unitMaster}
            {...formHandlers}
          />
        ))}
      </div>
      <div className="d-flex justify-content-between align-items-center px-3 py-3 border-top mt-2">
        <button
          type="button"
          className="btn btn-outline-primary px-4"
          onClick={formHandlers.handleAdd}
        >
          <i className="fas fa-plus me-1"></i> Add More
        </button>
        <button type="submit" className="btn btn-primary px-5">
          <i className="fas fa-save me-1"></i> Save
        </button>
      </div>
      </fieldset>
    </form>
  );
};

export default CreateSectorQuestionModal;